const GRAPHQL_URL = '/api/v1/catalog/graphql'

export interface CatalogItem {
  id: string
  code: string
  name: string
  shortName: string | null
  nature: 'GOODS' | 'SERVICES' | 'UTILITIES'
  hsnCode: string | null
  sacCode: string | null
  baseUom: { symbol: string | null; name: string } | null
}

/**
 * Returns the catalog item's preferred display label.
 *
 * `shortName` is optional — the GraphQL backend returns "" (empty string, not
 * null) when it's unset, so `??` falls through and keeps the empty value.
 * This helper treats empty/whitespace shortName as "missing" and falls back
 * to `name`.
 */
export function catalogItemDisplayName(item: { shortName?: string | null; name: string }): string {
  const trimmed = item.shortName?.trim()
  // `||` would also work but lint prefers `??`; here we explicitly need
  // empty-string-to-fallback semantics, which `??` doesn't give us.
  return trimmed !== undefined && trimmed !== '' ? trimmed : item.name
}

export interface CatalogUom {
  code: string
  name: string
  symbol: string | null
  category: string
}

const SEARCH_QUERY = `
  query SearchCatalogItems($search: String, $limit: Int, $nature: ItemNature) {
    items(
      filter: { search: $search, nature: $nature }
      pagination: { page: 1, perPage: $limit }
    ) {
      nodes {
        id code name shortName nature hsnCode sacCode
        baseUom { symbol name }
      }
    }
  }
`

const UOMS_QUERY = `
  query FetchActiveUoms {
    uoms(filter: { isActive: true }, pagination: { page: 1, perPage: 200 }) {
      nodes { code name symbol category }
    }
  }
`

async function gqlFetch<T>(body: object): Promise<T | null> {
  try {
    const res = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    })
    if (!res.ok) {return null}
    return res.json() as Promise<T>
  } catch {
    return null
  }
}

export async function searchCatalogItems(
  search: string,
  nature?: 'GOODS' | 'SERVICES',
  limit = 20,
): Promise<CatalogItem[]> {
  const json = await gqlFetch<{ data?: { items?: { nodes?: CatalogItem[] } } }>({
    query: SEARCH_QUERY,
    variables: { search, limit, nature: nature ?? null },
  })
  return json?.data?.items?.nodes ?? []
}

export async function fetchCatalogUoms(): Promise<CatalogUom[]> {
  const json = await gqlFetch<{ data?: { uoms?: { nodes?: CatalogUom[] } } }>({
    query: UOMS_QUERY,
  })
  return json?.data?.uoms?.nodes ?? []
}
